import { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import apiClient from "../services/api";
import { Fingerprint, X, Shield, Smartphone, Monitor } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { toast } from "sonner";
import { startRegistration } from "@simplewebauthn/browser";
// Detect platform for appropriate passkey branding
const getPlatformInfo = () => {
  const ua = navigator.userAgent || "";
  
  // Check iOS first (iPhone, iPad, iPod) - check userAgent which is more reliable
  const isIOS = /iPhone|iPad|iPod/.test(ua);
  
  // Check for Mac (but not iOS pretending to be Mac)
  // iPadOS 13+ reports as Mac, but has touch support
  const isMac = /Macintosh|Mac OS X/.test(ua) && !isIOS && !(navigator.maxTouchPoints > 1);
  
  // Check Windows
  const isWindows = /Windows/.test(ua);
  
  // Check Android
  const isAndroid = /Android/.test(ua);
  
  if (isIOS) {
    return {
      type: "apple",
      title: "Set Up Face ID Sign In",
      description: "Use Face ID or Touch ID for instant access next time.",
      buttonText: "Set Up Face ID",
      successText: "Next time you sign in, just use Face ID or Touch ID for instant access.",
      icon: "fingerprint"
    };
  } else if (isMac) {
    return {
      type: "apple",
      title: "Set Up Touch ID Sign In",
      description: "Use Touch ID for instant access next time.",
      buttonText: "Set Up Touch ID",
      successText: "Next time you sign in, just use Touch ID for instant access.",
      icon: "fingerprint"
    };
  } else if (isWindows) {
    return {
      type: "windows",
      title: "Set Up Windows Hello",
      description: "Use Windows Hello (face, fingerprint, or PIN) for instant access next time.",
      buttonText: "Set Up Windows Hello",
      successText: "Next time you sign in, just use Windows Hello for instant access.",
      icon: "shield"
    };
  } else if (isAndroid) {
    return {
      type: "android",
      title: "Set Up Biometric Sign In",
      description: "Use your fingerprint or face unlock for instant access next time.",
      buttonText: "Set Up Biometrics",
      successText: "Next time you sign in, just use your fingerprint or face for instant access.",
      icon: "fingerprint"
    };
  }
  
  return {
    type: "other",
    title: "Set Up Passkey",
    description: "Use biometric authentication for instant access next time.",
    buttonText: "Set Up Passkey",
    successText: "Next time you sign in, just use your passkey for instant access.",
    icon: "fingerprint"
  };
};

/**
 * PasskeySetupPrompt - Shows after successful password login to offer passkey setup
 * 
 * This component prompts users to set up a passkey for faster, more secure logins.
 * WebAuthn support is checked by the parent component before rendering.
 */
const PasskeySetupPrompt = ({ show, onClose }) => {
  const { token } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [hasExistingPasskey, setHasExistingPasskey] = useState(false);
  const [checkingPasskeys, setCheckingPasskeys] = useState(true);
  const [step, setStep] = useState("prompt"); // prompt, success, error
  const [platformInfo] = useState(() => getPlatformInfo());

  // Check if user already has a passkey
  useEffect(() => {
    const checkExistingPasskeys = async () => {
      if (!token) {
        setCheckingPasskeys(false);
        return;
      }
      
      try {
        const res = await apiClient.get(`/passkeys/list`);
        setHasExistingPasskey(res.data.length > 0);
      } catch (err) {
        console.debug("Could not check existing passkeys:", err);
      } finally {
        setCheckingPasskeys(false);
      }
    };

    if (show) {
      checkExistingPasskeys();
    }
  }, [show, token]);

  const handleSetupPasskey = async () => {
    setIsLoading(true);

    try {
      // Step 1: Get registration options from server
      const optionsRes = await apiClient.post(
        `/passkeys/registration-options`);
      const { options } = optionsRes.data;
      
      console.log("Got registration options:", options);

      // Step 2: Create credential using browser/OS (triggers Face ID/Touch ID)
      const credential = await startRegistration({ optionsJSON: options });

      if (!credential) {
        throw new Error("Passkey creation was cancelled");
      }
      
      console.log("Credential created:", credential.id);

      // Step 3: Verify and store credential on server
      const verifyRes = await apiClient.post(
        `/passkeys/registration-verify`,
        {
          id: credential.id,
          rawId: credential.rawId,
          response: {
            clientDataJSON: credential.response.clientDataJSON,
            attestationObject: credential.response.attestationObject,
            transports: credential.response.getTransports?.() || ["internal"],
          },
          type: credential.type,
          clientExtensionResults: credential.clientExtensionResults,
          authenticatorAttachment: credential.authenticatorAttachment,
        }
      );

      if (verifyRes.data.verified) {
        setStep("success");
        const successMsg = platformInfo.type === "windows" 
          ? "Windows Hello set up! You can now sign in instantly."
          : "Passkey created! You can now sign in with Face ID or Touch ID.";
        toast.success(successMsg);
      } else {
        throw new Error("Verification failed");
      }
    } catch (err) {
      console.error("Passkey setup error:", err);
      
      if (err.name === "NotAllowedError") {
        toast.error("Passkey setup was cancelled");
        onClose();
      } else if (err.name === "InvalidStateError") {
        toast.info("A passkey already exists for this account on this device");
        setStep("success");
      } else {
        const errorMsg = err.response?.data?.detail || err.message || "Failed to create passkey";
        toast.error(errorMsg);
        setStep("error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSkip = () => {
    sessionStorage.setItem("passkey_prompt_dismissed", "true");
    onClose();
  };

  // Don't show if still checking
  if (checkingPasskeys) {
    return null;
  }
  
  // Note: We allow multiple passkeys per user (one per device)
  // The hasExistingPasskey state is kept for potential future use
  // but doesn't block showing the prompt anymore

  return (
    <Dialog open={show} onOpenChange={(open) => !open && handleSkip()}>
      <DialogContent className="sm:max-w-md">
        {step === "prompt" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {platformInfo.type === "windows" ? (
                  <Shield className="w-5 h-5 text-primary" />
                ) : (
                  <Fingerprint className="w-5 h-5 text-primary" />
                )}
                {platformInfo.title}
              </DialogTitle>
              <DialogDescription>
                {platformInfo.description}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                <Shield className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium">More Secure</p>
                  <p className="text-xs text-muted-foreground">
                    {platformInfo.type === "windows" 
                      ? "Windows Hello credentials can't be stolen like passwords"
                      : "Passkeys are phishing-resistant and can't be stolen like passwords"}
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-3 p-3 bg-muted/50 rounded-lg">
                {platformInfo.type === "windows" ? (
                  <Monitor className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                ) : (
                  <Smartphone className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
                )}
                <div>
                  <p className="text-sm font-medium">
                    {platformInfo.type === "windows" ? "Quick & Easy" : "Works Everywhere"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {platformInfo.type === "windows"
                      ? "Sign in instantly with your face, fingerprint, or PIN"
                      : "Sign in on your phone, tablet, or computer with a single tap"}
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <Button
                onClick={handleSetupPasskey}
                disabled={isLoading}
                className="w-full gap-2"
                data-testid="setup-passkey-btn"
              >
                {platformInfo.type === "windows" ? (
                  <Shield className="w-4 h-4" />
                ) : (
                  <Fingerprint className="w-4 h-4" />
                )}
                {isLoading ? "Setting up..." : platformInfo.buttonText}
              </Button>
              <Button
                variant="ghost"
                onClick={handleSkip}
                className="w-full"
                data-testid="skip-passkey-btn"
              >
                Not now
              </Button>
            </div>
          </>
        )}

        {step === "success" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-green-600">
                <Shield className="w-5 h-5" />
                {platformInfo.type === "windows" ? "Windows Hello Set Up!" : "Passkey Created!"}
              </DialogTitle>
            </DialogHeader>

            <div className="py-4 text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
                {platformInfo.type === "windows" ? (
                  <Shield className="w-8 h-8 text-green-600" />
                ) : (
                  <Fingerprint className="w-8 h-8 text-green-600" />
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {platformInfo.successText}
              </p>
            </div>

            <Button onClick={onClose} className="w-full">
              Got it
            </Button>
          </>
        )}

        {step === "error" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <X className="w-5 h-5" />
                Setup Failed
              </DialogTitle>
            </DialogHeader>

            <div className="py-4 text-center">
              <p className="text-sm text-muted-foreground">
                We couldn't set up your passkey. You can try again later from Settings.
              </p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={handleSkip} className="flex-1">
                Skip for now
              </Button>
              <Button onClick={() => setStep("prompt")} className="flex-1">
                Try Again
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default PasskeySetupPrompt;
